import MfaRequiredOverride from 'types/MfaRequiredOverride';

/**
 * The single rule every save path applies: MfaRequiredOverride goes on the wire only when it is a
 * real boolean.
 *
 * Two separate reasons, both of which fail the whole save rather than just the field:
 *  - A null/absent value is a deliberate no-op server-side, so sending null adds nothing and only
 *    risks a 403 for a caller that is not allowed to write the field at all.
 *  - The employee and api-user endpoints are multipart/form-data built by appending every key of the
 *    payload object. FormData stringifies, so a null or undefined value would be sent as the literal
 *    "null"/"undefined" and fail model validation for a nullable boolean with a 400.
 */
export const shouldSendMfaOverride = (value: MfaRequiredOverride | undefined): value is boolean =>
  typeof value === 'boolean';

/**
 * What a save should actually put on the wire: the selected value, but only when it is a real
 * boolean *and* differs from what is already stored.
 *
 * Re-asserting the stored value would leave the setting correct but record an OverrideChanged
 * security event for a save that changed nothing - so editing a manager's phone number would land in
 * the MFA audit trail. Omitting it is what makes an unrelated edit a genuine no-op.
 */
export const getMfaOverrideToSend = (
  stored: MfaRequiredOverride | undefined,
  selected: MfaRequiredOverride | undefined,
): boolean | undefined => (shouldSendMfaOverride(selected) && selected !== stored ? selected : undefined);

/**
 * "No override" is not a payload value - it is reached by calling DELETE /UserMfa/{userId}/Override.
 * Only worth calling when the user actually had an override stored: a user without one has nothing
 * to clear, and a rejected or pointless call still lands in the server-side security event trail.
 */
export const shouldClearMfaOverride = (
  original: MfaRequiredOverride | undefined,
  current: MfaRequiredOverride | undefined,
): boolean => shouldSendMfaOverride(original) && current === null;
